import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search } from 'lucide-react';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import EquipmentDetailSummary from '../../components/EquipmentDetailSummary';
import { buildClientExtinguisherTableColumns } from '../../utils/clientEquipmentTableColumns';

const CLIENT_TABLE_EXCLUDE = ['clientName', 'clientCompany'];

const ExtinguisherInventoryView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewItem, setViewItem] = useState(null);
  const { error: showError } = useToast();

  const columns = useMemo(
    () => buildClientExtinguisherTableColumns((row) => setViewItem(row)),
    []
  );

  useEffect(() => {
    if (!user?.id) return;
    loadInventory();
  }, [user?.id]);

  useEffect(() => {
    applyFilters();
  }, [inventory, searchQuery]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      const data = await apiService.getExtinguisherInventory(user?.id, null, null);
      setInventory(data);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = inventory;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.inventoryNumber?.toLowerCase().includes(query) ||
          item.serialNumber?.toLowerCase().includes(query) ||
          item.detailedLocation?.toLowerCase().includes(query) ||
          item.location?.toLowerCase().includes(query) ||
          item.siteName?.toLowerCase().includes(query)
      );
    }

    setFilteredInventory(filtered);
  };

  const kpiStats = useMemo(() => {
    const now = new Date();
    let ok = 0;
    let urgent = 0;
    let expired = 0;
    filteredInventory.forEach((item) => {
      const next = item.nextMaintenanceDate ? new Date(item.nextMaintenanceDate) : null;
      if (!next) return;
      const days = (next - now) / (1000 * 60 * 60 * 24);
      if (days < 0) expired += 1;
      else if (days <= 10) urgent += 1;
      else if (days > 30) ok += 1;
    });
    return { total: filteredInventory.length, ok, urgent, expired };
  }, [filteredInventory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-red-600" />
          État des Extincteurs
        </h1>
        <p className="text-gray-600 mt-1">
          Vue synthétique — ouvrez « Voir » ou la fiche complète pour tous les détails
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{kpiStats.total}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Total</p>
          </div>
        </Card>
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-green-600">{kpiStats.ok}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">OK</p>
          </div>
        </Card>
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-amber-600">{kpiStats.urgent}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Urgent</p>
          </div>
        </Card>
        <Card>
          <div className="text-center py-1">
            <p className="text-2xl sm:text-3xl font-bold text-red-600">{kpiStats.expired}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Expirés</p>
          </div>
        </Card>
      </div>

      <Card>
        <Input
          placeholder="Rechercher par N° inventaire, N° série, site, emplacement…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </Card>

      <Card className="p-0 sm:p-0 overflow-hidden w-full min-w-0">
        <Table
          columns={columns}
          data={filteredInventory}
          emptyMessage="Aucun extincteur ne correspond à votre recherche"
        />
      </Card>

      <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="Détails extincteur">
        {viewItem && (
          <div className="space-y-4">
            <div className="max-h-[60vh] overflow-y-auto">
              <EquipmentDetailSummary equipment={viewItem} columns={2} dense excludeKeys={CLIENT_TABLE_EXCLUDE} />
            </div>
            <Button
              variant="primary"
              className="w-full"
              onClick={() => {
                setViewItem(null);
                navigate(`/my-equipment/${viewItem.id}`);
              }}
            >
              Ouvrir la fiche complète
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExtinguisherInventoryView;
